/**
 * 게시판 관리 컴포넌트
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../../../shared/services/api';
import { BoardListItem, BoardItem } from '../../../../shared/types/board.types';
import { Button } from '../../../../shared/components/UI/Button';
import { Card } from '../../../../shared/components/UI/Card';
import { boardService } from '../../services/boardService';
import { invalidateBoardCache } from '../../../../shared/utils/cacheInvalidation';
import styles from './BoardManager.module.css';

interface BoardManagerProps {
  boardType: 'sunday-school' | 'news' | 'gallery' | 'bulletin';
  boardLabel: string;
}

export const BoardManager: React.FC<BoardManagerProps> = ({ boardType, boardLabel }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BoardListItem[]>([]);
  const [selectedPost, setSelectedPost] = useState<BoardItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadPosts();
  }, [boardType, page]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const response = await boardService.getPosts(boardType, page, 10);
      setPosts(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      console.error('Failed to load posts:', error);
      // 401 에러는 인터셉터에서 처리하므로 alert 표시하지 않음
      if (error?.response?.status !== 401) {
        alert('게시글 목록을 불러오는데 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = async (id: number) => {
    try {
      const post = await boardService.getPost(boardType, id);
      setSelectedPost(post);
    } catch (error: any) {
      console.error('Failed to load post detail:', error);
      // 401 에러는 인터셉터에서 처리하므로 alert 표시하지 않음
      if (error?.response?.status !== 401) {
        alert('게시글을 불러오는데 실패했습니다.');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await apiClient.delete(`/board/${boardType}/${id}`);
      alert('삭제되었습니다.');
      setSelectedPost(null);

      // 캐시 무효화 (사용자 페이지의 게시판 데이터 갱신)
      invalidateBoardCache(boardType);

      loadPosts();
    } catch (error: any) {
      console.error('Failed to delete post:', error);
      // 401 에러는 인터셉터에서 처리하므로 alert 표시하지 않음
      if (error?.response?.status !== 401) {
        alert('삭제에 실패했습니다.');
      }
    }
  };

  const handleCreate = () => {
    navigate(`/admin/board/${boardType}/new`);
  };

  const handleEdit = (id: number) => {
    navigate(`/admin/board/${boardType}/${id}/edit`);
  };

  const handleCloseDetail = () => {
    setSelectedPost(null);
  };

  return (
    <div className={styles.boardManager}>
      <div className={styles.header}>
        <h1>{boardLabel} 게시판 관리</h1>
        <Button onClick={handleCreate}>새 게시글 작성</Button>
      </div>

      <Card className={styles.tableCard}>
        {loading ? (
          <p className={styles.emptyMessage}></p>
        ) : posts.length === 0 ? (
          <p className={styles.emptyMessage}></p>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>번호</th>
                  <th>제목</th>
                  <th>조회수</th>
                  <th>작성일</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post, index) => (
                  <tr key={post.id} onClick={() => handleRowClick(post.id)} className={styles.tableRow}>
                    <td>{(page - 1) * 10 + index + 1}</td>
                    <td className={styles.titleCell}>{post.title}</td>
                    <td>{post.views}</td>
                    <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            이전
          </Button>
          <span className={styles.pageInfo}>
            {page} / {totalPages}
          </span>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            다음
          </Button>
        </div>
      )}

      {selectedPost && (
        <div className={styles.modal} onClick={handleCloseDetail}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={handleCloseDetail}>✕</button>

            <div className={styles.detailContainer}>
              <div className={styles.detailHeader}>
                <h1 className={styles.detailTitle}>{selectedPost.title}</h1>
                <div className={styles.detailMeta}>
                  <span>작성일: {new Date(selectedPost.createdAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                  <span>조회수: {selectedPost.views}</span>
                </div>
              </div>

              <div className={styles.detailContent}>
                {selectedPost.content}
              </div>

              {selectedPost.files && selectedPost.files.length > 0 && (
                <div className={styles.imageGallery}>
                  {selectedPost.files.map((file) => (
                    <img
                      key={file.id}
                      src={file.fileUrl}
                      alt={file.originalFileName}
                      className={styles.detailImage}
                    />
                  ))}
                </div>
              )}

              <div className={styles.detailActions}>
                <Button onClick={() => handleEdit(selectedPost.id)}>수정</Button>
                <Button onClick={() => handleDelete(selectedPost.id)}>삭제</Button>
                <Button onClick={handleCloseDetail}>목록으로</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
